import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormGroup, Validators, ValidatorFn } from '@angular/forms';
import { AuthService } from 'src/app/core/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { DatePipe } from '@angular/common';
import { forkJoin } from 'rxjs';
import { Router } from '@angular/router';
import { VG_DATE_PROVIDERS } from '../../vg-datepicker/vg-datepicker';

@Component({
    selector: 'app-edit-health-dialog',
    templateUrl: './edit-health-dialog.component.html',
    styleUrls: ['./edit-health-dialog.component.css'],
    providers: VG_DATE_PROVIDERS,   // dates display as "Oct 13, 2026"
    standalone: false
})
export class HealthEditDialogComponent {
  healthForm: FormGroup;

  bloodGroups: any[] = [];
  ethnicities: any[]=[];
  country:any;
  height:any;
  weight:any;
  profileData:any;
  gender_list = ["Female","Male","Others"];
  maxDate = new Date();
  subitted = false;
  profileId: any;               // id of the existing health-profile record (decides update vs create)
  isSaving = false;
  profileReady = false;         // Save stays disabled until the existing profile has been loaded (else an edit could be sent as a create)
  private pendingLoads = 2;     // health profile + digicoffer profile

  get f() {
    return this.healthForm.controls;
  }

  // Native min / max of the number inputs follow the selected unit, so the spinner arrows start at the
  // minimum valid value (an empty field + up arrow used to give min(0) + step = 0.01).
  get heightMin(): number { return HEIGHT_LIMITS[this.healthForm?.value?.heightUnit || 'cm']?.min ?? 0; }
  get heightMax(): number | null { return HEIGHT_LIMITS[this.healthForm?.value?.heightUnit || 'cm']?.max ?? null; }
  get weightMin(): number { return WEIGHT_LIMITS[this.healthForm?.value?.weigthtUnit || 'kg']?.min ?? 0; }
  get weightMax(): number | null { return WEIGHT_LIMITS[this.healthForm?.value?.weigthtUnit || 'kg']?.max ?? null; }
  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<HealthEditDialogComponent>,
    public auth: AuthService, private router: Router,
    private toastr: ToastrService,
    private datePipe: DatePipe,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {

    this.healthForm = this.fb.group({
      // height: [data?.height || '', [Validators.required, Validators.min(1), Validators.max(299),  Validators.pattern(/^\d{1,3}(\.\d{1,2})?$/) ]],
      // weight: [data?.weight || '', [Validators.required, Validators.min(1), Validators.max(999),  Validators.pattern(/^\d{1,3}(\.\d{1,2})?$/) ]],
      height: [
        data?.height || '', 
        [
          Validators.required, 
          Validators.pattern(/^\d{1,3}(\.\d{1,2})?$/),
          heightValidator(() => this.healthForm?.value?.heightUnit || 'cm') // <-- dynamic
        ]
      ],
      weight: [
        data?.weight || '', 
        [
          Validators.required, 
          Validators.pattern(/^\d{1,3}(\.\d{1,2})?$/),
          weightValidator(() => this.healthForm?.value?.weigthtUnit || 'kg') // <-- dynamic
        ]
      ],
      bloodGroup: [data?.bloodGroup || '', Validators.required],
      ethnicity: [data?.ethnicity || '', Validators.required],
      heightUnit:[data?.heightUnit || 'cm'],
      weigthtUnit:[data?.weigthtUnit || 'kg'],
      gender:[data?.gender || '', Validators.required],
      dob:[data?.dob ? new Date(data.dob) : '', Validators.required],

    });
  }
   
  ngOnInit() {
    console.log('data',this.data)
    this.getEthnicity();
    this.getBloodGroup();
    this.getUnits();
    this.loadExistingHealthProfile();

    this.healthForm.get('heightUnit')?.valueChanges.subscribe((unit: string) => {
      this.height = unit;  // update variable bound to label
      const heightCtrl = this.healthForm.get('height');

      if (heightCtrl) {
        // Trigger validation with current value
        heightCtrl.setValue(heightCtrl.value);
        heightCtrl.updateValueAndValidity();
      }
    });

    this.healthForm.get('weigthtUnit')?.valueChanges.subscribe((unit: string) => {
      this.weight = unit;  // update variable bound to label
      const weightCtrl = this.healthForm.get('weight');
      if (weightCtrl) {
        // Trigger validation with current value
        weightCtrl.setValue(weightCtrl. value);
        weightCtrl.updateValueAndValidity();
      }
    });
  }

  onCancel() {
    this.dialogRef.close();
  }

  onNumberInput(event: Event, ctrlName: string) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // regex to allow numbers with up to 2 decimals
    const regex = /^\d*\.?\d{0,2}$/;

    if (!regex.test(value)) {
      // remove the last character if it exceeds 2 decimals
      input.value = value.slice(0, -1);
      // update form control
      this.healthForm.get(ctrlName)?.setValue(input.value, { emitEvent: false });
    }
  }


  getError(ctrlName: any): any {
    const ctrl: AbstractControl | null = this.healthForm.get(ctrlName as string);
    if (!ctrl) return null;

    // show errors only after submit or user interaction
    const shouldShow = this.subitted || ctrl.touched || ctrl.dirty;
    if (!shouldShow || !ctrl.errors) return null;

    const e = ctrl.errors;
    if (e['required']) return 'This field is required';
    // if (e['pattern'])  return 'Only digits are allowed';
    // if (e['min'])      return `Minimum value is ${e['min'].min}`;
    // if (e['max'])      return `Maximum value is ${e['max'].max}`;
    // if (e['pattern']) {
    //   return `${ctrlName} must be a valid number (up to 2 decimal places)`;
    // }
    if (e['heightRange']) return e['heightRange'];
    if (e['weightRange']) return e['weightRange'];

    return 'Enter a valid value';
  }

  onSave(): void {
    this.subitted = true; // (fix typo from subitted)

    if (this.healthForm.invalid) {
      this.healthForm.markAllAsTouched();
      return;
    }

  if (!this.healthForm.valid) {
    this.toastr.error('Please fix the errors before saving.');
    return;
  }
    // console.log('Form Values:', this.healthForm.value);
    // console.log('Profile Data:', this.profileData);
    // console.log('Data ID:', this.data);
    
  // Format DOB only if present
  const rawDob = this.healthForm.value.dob;
  const dob = rawDob ? (this.datePipe.transform(rawDob, 'MM-dd-yyyy') || '') : '';
  const dob2 = rawDob ? (this.datePipe.transform(rawDob, 'dd-MM-yyyy') || '') : '';

  // Build payload once
  const payload = {
    gender: this.healthForm.value.gender,
    height: Number(this.healthForm.value.height),
    weight: Number(this.healthForm.value.weight),
    ethnicity: this.healthForm.value.ethnicity,
    bloodGroup: this.healthForm.value.bloodGroup,
    firstName: this.profileData?.first_name || localStorage.getItem('first_name') || '',
    lastName: this.profileData?.last_name || localStorage.getItem('last_name') || '',
    heightUnit: this.healthForm.value.heightUnit,   // unit label/string
    weigthtUnit: this.healthForm.value.weigthtUnit,   // fix typo: was 'weigthtUnit'
    dob,
    addon: []
  };

  // Trimmed version for digicoffer
  const digicofferPayload = {
    first_name: payload.firstName,
    last_name: payload.lastName,
    dob: dob2,
    gender: payload.gender.toLowerCase()
  };

  // Update if ID exists, else create
  const recordId = this.data?.id || this.profileId;   // the dashboard data has no id: it comes from the saved health profile
  const hasId = !!recordId;

  const req$ = hasId
  ? forkJoin([
      this.auth.update_profile_list(recordId, payload),
      this.auth.updateProfile(digicofferPayload)
    ])
  : 
    forkJoin([
      this.auth.create_profile_list(payload),
      this.auth.updateProfile(digicofferPayload)
    ])

  this.isSaving = true;
  req$.subscribe({
    next: (res: any) => {
      console.log('Response:', res);
      this.isSaving = false;
      this.toastr.success(
        hasId ? 'Health information updated successfully!' : 'Health information created successfully!'
      );
      // Close with the saved values: the opener refreshes the profile from the API (no page reload,
      // which used to cancel these requests before they finished and hid them from the Network tab).
      this.dialogRef.close(this.healthForm.value);
    },
    error: (err: any) => {
      console.error(err);
      this.isSaving = false;
      this.toastr.error('Something went wrong. Please try again.');
    }
  });
}

  // refreshRoute() {
  //   console.log('Refreshing route...');
  //   const currentUrl = this.router.url;
  //   this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
  //     this.router.navigate([currentUrl]);
  //   });
  // }

  /**
   * The dashboard data passed to this dialog has no id / gender / ethnicity, so the dialog loads the saved
   * health profile itself. Only controls the user has not touched and that are still empty are filled.
   */
  private loadExistingHealthProfile(): void {
    this.auth.profile_details_all().subscribe((res: any) => {
      const p = Array.isArray(res) ? res[0] : (res?.data?.profile || res?.data || res);
      if (p) {
        this.profileId = this.profileId || p.id;
        this.fillIfEmpty('height', p.height);
        this.fillIfEmpty('weight', p.weight);
        this.fillIfEmpty('bloodGroup', p.bloodGroup);
        this.fillIfEmpty('ethnicity', p.ethnicity);
        this.fillIfEmpty('gender', this.matchGender(p.gender));
        this.fillIfEmpty('dob', this.toDate(p.dob, false));
      }
      this.settleLoad();
    }, () => this.settleLoad());
  }

  private settleLoad(): void {
    if (--this.pendingLoads <= 0) { this.profileReady = true; }
  }

  private fillIfEmpty(name: string, value: any): void {
    const ctrl = this.healthForm.get(name);
    if (!ctrl || ctrl.dirty) { return; }
    const cur = ctrl.value;
    if (cur !== '' && cur !== null && cur !== undefined) { return; }
    if (value === null || value === undefined || value === '') { return; }
    ctrl.setValue(value);
  }

  /** 'male' / 'Male' / 'other' -> the option text used by the dropdown, '' when unknown. */
  private matchGender(raw: any): string {
    const s = String(raw ?? '').trim().toLowerCase();
    if (!s) { return ''; }
    if (s === 'other') { return 'Others'; }
    return this.gender_list.find(g => g.toLowerCase() === s) || '';
  }

  /** Health profile dob is 'MM-dd-yyyy'; the account profile may send 'dd-MM-yyyy' (what this dialog sends to it) or an ISO date. */
  private toDate(raw: any, accountProfile: boolean): Date | '' {
    if (!raw) { return ''; }
    const s = String(raw).trim();
    const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(s);
    const d = m
      ? (accountProfile ? new Date(+m[3], +m[2] - 1, +m[1]) : new Date(+m[3], +m[1] - 1, +m[2]))
      : new Date(s);
    return isNaN(d.getTime()) ? '' : d;
  }

  getEthnicity(){
    this.auth.get_ethnicity().subscribe((res:any) =>{
      this.ethnicities = res.data.ethnicity;

    });

  }
  getBloodGroup(){
    this.auth.get_bloodgrps().subscribe((res:any) =>{
      this.bloodGroups = res.data.bloodgroup;
  
    });

  }
  getUnits(){

     this.auth.get_digicoffer_profile().subscribe((user: any) => {
      this.profileData = user.data?.profile;
      // gender / dob also live in the account profile: use them for whatever is still empty
      this.fillIfEmpty('gender', this.matchGender(this.profileData?.gender));
      this.fillIfEmpty('dob', this.toDate(this.profileData?.dob, true));
      this.settleLoad();
    }, () => this.settleLoad())

    // this.auth.get_citizenship().subscribe((res:any)=>{
    //   if(res.error==false){
    //     for (let i = 0; i < res.data.citizenships.length; i++) {

    //       if(res.data.citizenships[i].index == "citizen_primary"){
    //         this.country = res.data.citizenships[i].country  
    //         if(this.country== "usa" || this.country == "united states of america"||this.country=="USA"){
    //           this.height ="feet"
    //           this.weight="lbs"
    //           this.healthForm.patchValue({
    //             heightUnit: this.height,
    //             weigthtUnit: this.weight
    //           });

    //         } else {
    //           this.height ="cm"
    //           this.weight="kg"
    //           this.healthForm.patchValue({
    //             heightUnit: this.height,
    //             weigthtUnit: this.weight
    //           });

    //            }


    //       }
    //     }
       
        

  
     

        
        
    //   }

    // })
    this.height = this.data?.heightUnit || 'cm'
    this.weight = this.data?.weigthtUnit || 'kg'
     
  }
   
   
}



/** Allowed range per unit: single source for the validators below and for the min / max of the inputs. */
export const HEIGHT_LIMITS: { [unit: string]: { min: number; max: number; label: string } } = {
  cm:   { min: 30,   max: 272, label: 'cm' },
  inch: { min: 11.8, max: 107, label: 'inches' },
  feet: { min: 1,    max: 8,   label: 'feet' }
};

export const WEIGHT_LIMITS: { [unit: string]: { min: number; max: number; label: string } } = {
  kg:  { min: 2,   max: 300, label: 'kg' },
  lbs: { min: 4.4, max: 660, label: 'lbs' }
};

export function heightValidator(getUnit: () => string): ValidatorFn {
  return (control: AbstractControl) => {
    if (control.value === null || control.value === '') return null;

    const val = Number(control.value);
    const limits = HEIGHT_LIMITS[getUnit()];

    if (limits && (val < limits.min || val > limits.max)) {
      return { heightRange: `Height must be between ${limits.min} and ${limits.max} ${limits.label}` };
    }

    return null; // valid
  };
}

export function weightValidator(getUnit: () => string): ValidatorFn {
  return (control: AbstractControl) => {
    if (control.value === null || control.value === '') return null;

    const val = Number(control.value);
    const limits = WEIGHT_LIMITS[getUnit()];

    if (limits && (val < limits.min || val > limits.max)) {
      return { weightRange: `Weight must be between ${limits.min} and ${limits.max} ${limits.label}` };
    }

    return null;
  };
}